'use strict';

const libPictProvider = require('pict-provider');
const libNavigation = require('headlight-entity-navigation');
const libHosts = require('headlight-qrcode/source/Headlight-QRCode-Hosts.js');
const { DEFAULT_SAMPLE_STATUS_SELECT_OPTIONS } = require('headlight-dynamic-inputs-bundle/source/providers/_VocabularySelectorMap.js');

/**
 * @typedef {import('headlight-entity-navigation/source/Headlight-Entity-Navigation.js').DestinationRule} DestinationRule
 */

/**
 * What each entity's rules can test and where they can send people. Route templates are fixed per
 * target so nobody types hosts or paths; `Custom` keeps an admin-written route on a known app.
 *
 * @type {Record<string, { Label: string, Conditions: Array<{ Address: string, Label: string, Values?: 'Statuses' }>, Targets: Array<{ Key: string, Label: string, Target: Object }>, Example: Object }>}
 */
const ENTITIES =
{
	Sample:
	{
		Label: 'Samples',
		Conditions: [ { Address: 'Record.Status', Label: 'Sample status', Values: 'Statuses' } ],
		Targets:
		[
			{ Key: 'lims', Label: 'The sample in LIMS', Target: { App: 'LIMS', Route: '#/Sample/Workspace/{~D:Record.IDSample~}' } },
			{ Key: 'workspace', Label: 'The sample workspace (configuration app)', Target: { App: 'Config', Route: '#/Sample/Workspace/{~D:Record.IDSample~}' } },
			{ Key: 'form-oldest', Label: 'The sample’s first form', Target: { Via: 'Document', Pick: 'Oldest' } },
			{ Key: 'form-newest', Label: 'The sample’s latest form', Target: { Via: 'Document', Pick: 'Newest' } },
			{ Key: 'landing', Label: 'A summary page with choices', Target: { Landing: true } },
		],
		Example: { GUIDSample: 'EXAMPLE-SAMPLE', IDSample: 1, Status: 'RECV' },
	},
	Location:
	{
		Label: 'Locations',
		Conditions: [ { Address: 'Record.Type', Label: 'Location type' } ],
		Targets:
		[
			{ Key: 'lims', Label: 'The location in LIMS', Target: { App: 'LIMS', Route: '#/Location/Workspace/{~D:Record.IDLocation~}' } },
			{ Key: 'landing', Label: 'A summary page with choices', Target: { Landing: true } },
		],
		Example: { GUIDLocation: 'EXAMPLE-LOCATION', IDLocation: 1, Type: '' },
	},
	Document:
	{
		Label: 'Forms',
		Conditions: [ { Address: 'Record.DocumentType', Label: 'Form type' } ],
		Targets:
		[
			{ Key: 'landing', Label: 'A summary page with choices', Target: { Landing: true } },
		],
		Example: { IDDocument: 1, GUIDDocument: 'EXAMPLE-FORM', DocumentType: '' },
	},
};

const CUSTOM_TARGET_KEY = 'custom';

/** How many role matches the role search lists at once. */
const ROLE_SUGGESTION_LIMIT = 8;

/**
 * EntityDestinationsConfig — the #/EntityDestinations screen ("Where Records Open"). Edits the customer-level
 * `EntityDestinations` configuration (NamespaceValue = entity) that the links app resolves scanned labels and
 * emailed links against, with a "who goes where" preview using the same resolver.
 *
 * Platform rules (Global level) apply to every customer. They are shown read-only, after the customer's own rules
 * (which are kept ordered ahead of them), and a customer can only turn one off. The configuration service stores
 * only what differs from the Global level, so saving the merged map keeps the platform rules inherited.
 * Gated by the `EntityDestinations-Configure` entitlement. Super-users may pick any customer.
 */
class EntityDestinationsConfigProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get state()
	{
		if (!this.pict.AppData.EntityDestinationsConfig)
		{
			this.pict.AppData.EntityDestinationsConfig =
			{
				Loaded: false, Loading: false, Saving: false, Error: '', Message: '',
				TargetCustomerID: 0, TargetCustomerName: '', Customers: [],
				Entity: 'Sample', Rules: {}, Platform: {}, Dirty: {},
				Roles: [], Statuses: [],
				EditingKey: '', Draft: null,
				PreviewRoles: [], PreviewValue: '',
				RoleSearch: { Draft: '', Preview: '' },
			};
		}
		return this.pict.AppData.EntityDestinationsConfig;
	}

	get currentUser()
	{
		return this.pict.AppData.CurrentUser || {};
	}

	callerCustomer()
	{
		return Number(this.currentUser.CustomerID) || 0;
	}

	isSuperUser()
	{
		return (Number(this.currentUser.UserRoleIndex) === 5) && (Number(this.currentUser.CustomerID) === 1);
	}

	can()
	{
		const tmpService = this.pict.fable.EffectivePermissionsService || (this.fable && this.fable.EffectivePermissionsService);
		if (!tmpService || (typeof tmpService.can !== 'function'))
		{
			return true;
		}
		return tmpService.can('EntityDestinations', 'Configure');
	}

	/**
	 * @param {string} pURL
	 * @return {Promise<any>} the body, or null on error
	 */
	_getJSON(pURL)
	{
		const tmpClient = this.restClient;
		if (!tmpClient)
		{
			return Promise.resolve(null);
		}
		return new Promise((fResolve) => tmpClient.getJSON(pURL, (pError, pBody) => fResolve(pError ? null : pBody)));
	}

	/**
	 * @param {string} pURL
	 * @param {Object} pBody
	 * @return {Promise<{ error: any, result: any }>}
	 */
	_postJSON(pURL, pBody)
	{
		const tmpClient = this.restClient;
		if (!tmpClient)
		{
			return Promise.resolve({ error: 'no client', result: null });
		}
		return new Promise((fResolve) => tmpClient.postJSON(pURL, pBody, (pError, pResult) => fResolve({ error: pError, result: pResult })));
	}

	/**
	 * @param {any} pValue
	 * @return {string}
	 */
	_esc(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
	}

	/** @return {Record<string, DestinationRule>} the rules of the entity being edited */
	currentRules()
	{
		const tmpState = this.state;
		if (!tmpState.Rules[tmpState.Entity] || typeof tmpState.Rules[tmpState.Entity] !== 'object')
		{
			tmpState.Rules[tmpState.Entity] = {};
		}
		return tmpState.Rules[tmpState.Entity];
	}

	/** @return {Array<[string, DestinationRule]>} rules in the order they are tried */
	orderedRules()
	{
		return Object.entries(this.currentRules()).sort(([ pKeyA, pA ], [ pKeyB, pB ]) => ((pA.Order ?? 100) - (pB.Order ?? 100)) || pKeyA.localeCompare(pKeyB));
	}

	/**
	 * @param {string} pKey
	 * @return {boolean} true for a rule the platform (Global level) sets, which this customer inherits
	 */
	isPlatformRule(pKey)
	{
		const tmpPlatform = this.state.Platform[this.state.Entity];
		return !!(tmpPlatform && tmpPlatform[pKey]);
	}

	/** @return {Array<[string, DestinationRule]>} the customer's own rules, in the order they are tried */
	customerRules()
	{
		return this.orderedRules().filter(([ pKey ]) => !this.isPlatformRule(pKey));
	}

	async loadAsync()
	{
		const tmpState = this.state;
		tmpState.Loading = true;
		tmpState.Error = '';
		tmpState.Message = '';
		this.render();
		try
		{
			if (this.isSuperUser())
			{
				const tmpCustomers = await this._getJSON('Customers/FilteredTo/FBV~Deleted~EQ~0~FSF~Name~ASC~0/0/2000');
				tmpState.Customers = Array.isArray(tmpCustomers) ? tmpCustomers.map((pCustomer) => ({ IDCustomer: pCustomer.IDCustomer, Name: pCustomer.Name })) : [];
			}
			if (!tmpState.TargetCustomerID)
			{
				tmpState.TargetCustomerID = this.callerCustomer();
			}
			await this._loadCustomerAsync(tmpState.TargetCustomerID);
			tmpState.Loaded = true;
		}
		catch (pError)
		{
			tmpState.Error = `Could not load where records open: ${(pError && pError.message) || pError}`;
		}
		finally
		{
			tmpState.Loading = false;
			this.render();
		}
	}

	/**
	 * @param {number} pIDCustomer
	 */
	async _loadCustomerAsync(pIDCustomer)
	{
		const tmpState = this.state;
		const tmpCustomer = await this._getJSON(`Customer/${Number(pIDCustomer)}`);
		tmpState.TargetCustomerName = (tmpCustomer && tmpCustomer.Name) || '';
		const tmpMap = await this._getJSON(`Configuration/For/EntityDestinations/In/Customer/${Number(pIDCustomer)}`);
		if (tmpMap === null)
		{
			throw new Error('the saved settings could not be read');
		}
		// Global level only: which of the merged rules come from the platform.
		const tmpPlatformMap = await this._getJSON('Configuration/For/EntityDestinations/In/Global/0');
		if (tmpPlatformMap === null)
		{
			throw new Error('the platform’s rules could not be read');
		}
		tmpState.Rules = {};
		tmpState.Platform = {};
		for (const tmpEntity of Object.keys(ENTITIES))
		{
			tmpState.Rules[tmpEntity] = (tmpMap && tmpMap[tmpEntity] && typeof tmpMap[tmpEntity] === 'object') ? JSON.parse(JSON.stringify(tmpMap[tmpEntity])) : {};
			tmpState.Platform[tmpEntity] = (tmpPlatformMap && tmpPlatformMap[tmpEntity] && typeof tmpPlatformMap[tmpEntity] === 'object') ? JSON.parse(JSON.stringify(tmpPlatformMap[tmpEntity])) : {};
		}
		tmpState.Dirty = {};
		tmpState.EditingKey = '';
		tmpState.Draft = null;
		const tmpRoles = await this._getJSON(`Roles/FilteredTo/FBL~IDCustomer~INN~0,${Number(pIDCustomer)}~FSF~Name~ASC~0/0/1000`);
		const tmpRoleRows = (Array.isArray(tmpRoles) ? tmpRoles : []).filter((pRole) => pRole && pRole.GUIDRole && pRole.Name && !pRole.Deleted && pRole.Type !== 'PermissionGroup');
		const tmpNameCounts = tmpRoleRows.reduce((pCounts, pRole) => Object.assign(pCounts, { [pRole.Name]: (pCounts[pRole.Name] || 0) + 1 }), {});
		tmpState.Roles = tmpRoleRows.map((pRole) => ({ Hash: pRole.GUIDRole, Name: (tmpNameCounts[pRole.Name] > 1 && !Number(pRole.IDCustomer)) ? `${pRole.Name} (standard)` : pRole.Name, PermissionLevel: pRole.PermissionLevel || '' }));
		const tmpStatuses = await this._getJSON('SampleStatuss/FilteredTo/FSF~Ordinal~ASC~0/0/500');
		tmpState.Statuses = (Array.isArray(tmpStatuses) && tmpStatuses.length)
			? tmpStatuses.map((pStatus) => ({ Hash: pStatus.Hash, Name: pStatus.Name || pStatus.Hash }))
			: DEFAULT_SAMPLE_STATUS_SELECT_OPTIONS.map((pOption) => ({ Hash: pOption.id, Name: pOption.text }));
		tmpState.PreviewRoles = [];
		tmpState.PreviewValue = '';
		tmpState.RoleSearch = { Draft: '', Preview: '' };
	}

	async selectCustomer(pIDCustomer)
	{
		const tmpState = this.state;
		tmpState.TargetCustomerID = Number(pIDCustomer) || 0;
		tmpState.Message = '';
		tmpState.Error = '';
		tmpState.Loading = true;
		this.render();
		try
		{
			await this._loadCustomerAsync(tmpState.TargetCustomerID);
		}
		catch (pError)
		{
			tmpState.Error = `Could not load where records open: ${(pError && pError.message) || pError}`;
		}
		tmpState.Loading = false;
		this.render();
	}

	setEntity(pEntity)
	{
		if (ENTITIES[pEntity])
		{
			this.state.Entity = pEntity;
			this.state.EditingKey = '';
			this.state.Draft = null;
			this.state.PreviewValue = '';
			this.render();
		}
	}

	// --- Rule list ------------------------------------------------------------------------------------------

	_markDirty()
	{
		this.state.Dirty[this.state.Entity] = true;
		this.state.Message = '';
	}

	/**
	 * Number the customer's own rules 10, 20, … so they are tried in the order shown, and always ahead of the
	 * platform rules (the gap shrinks if a platform rule's Order is low). Platform rules keep their own Order.
	 * @param {Array<[string, DestinationRule]>} pOrdered - The customer's rules, in the order wanted.
	 */
	_renumber(pOrdered)
	{
		const tmpPlatformOrders = Object.values(this.state.Platform[this.state.Entity] || {}).filter((pRule) => pRule && typeof pRule === 'object').map((pRule) => pRule.Order ?? 100);
		const tmpCeiling = tmpPlatformOrders.length ? Math.min(...tmpPlatformOrders) : Infinity;
		const tmpStep = Number.isFinite(tmpCeiling) ? Math.max(Math.min(10, (tmpCeiling - 1) / (pOrdered.length || 1)), 0.001) : 10;
		pOrdered.forEach(([ , pRule ], pIndex) =>
		{
			pRule.Order = Math.round((pIndex + 1) * tmpStep * 1000) / 1000;
		});
	}

	moveRule(pKey, pDelta)
	{
		const tmpOrdered = this.customerRules();
		const tmpIndex = tmpOrdered.findIndex(([ pRuleKey ]) => pRuleKey === pKey);
		const tmpTarget = tmpIndex + Number(pDelta);
		if (tmpIndex < 0 || tmpTarget < 0 || tmpTarget >= tmpOrdered.length)
		{
			return;
		}
		const [ tmpMoved ] = tmpOrdered.splice(tmpIndex, 1);
		tmpOrdered.splice(tmpTarget, 0, tmpMoved);
		this._renumber(tmpOrdered);
		this._markDirty();
		this.render();
	}

	toggleRule(pKey)
	{
		const tmpRule = this.currentRules()[pKey];
		if (tmpRule)
		{
			tmpRule.Enabled = (tmpRule.Enabled === false);
			this._markDirty();
			this.render();
		}
	}

	removeRule(pKey)
	{
		if (this.isPlatformRule(pKey))
		{
			return;
		}
		delete this.currentRules()[pKey];
		if (this.state.EditingKey === pKey)
		{
			this.state.EditingKey = '';
			this.state.Draft = null;
		}
		this._renumber(this.customerRules());
		this._markDirty();
		this.render();
	}

	// --- Rule editor ----------------------------------------------------------------------------------------

	/**
	 * @param {DestinationRule['Target']} pTarget
	 * @return {string} the preset key matching a target, or 'custom'
	 */
	_targetKey(pTarget)
	{
		const tmpPreset = ENTITIES[this.state.Entity].Targets.find((pChoice) => JSON.stringify(pChoice.Target) === JSON.stringify(pTarget || {}));
		return tmpPreset ? tmpPreset.Key : CUSTOM_TARGET_KEY;
	}

	editRule(pKey)
	{
		const tmpRule = this.currentRules()[pKey];
		if (!tmpRule || this.isPlatformRule(pKey))
		{
			return;
		}
		const tmpCondition = ENTITIES[this.state.Entity].Conditions[0];
		const tmpValues = (tmpRule.When && tmpRule.When[tmpCondition.Address]) || [];
		this.state.EditingKey = pKey;
		this.state.RoleSearch.Draft = '';
		this.state.Draft =
		{
			Label: tmpRule.Label || '',
			Roles: (tmpRule.Audience && tmpRule.Audience.Roles) ? tmpRule.Audience.Roles.slice() : [],
			Values: Array.isArray(tmpValues) ? tmpValues.map(String) : [ String(tmpValues) ],
			TargetKey: this._targetKey(tmpRule.Target),
			CustomApp: (tmpRule.Target && tmpRule.Target.App) || 'LIMS',
			CustomRoute: (tmpRule.Target && tmpRule.Target.Route) || '',
			Extra: { Audience: Object.assign({}, tmpRule.Audience || {}), When: Object.assign({}, tmpRule.When || {}) },
		};
		this.render();
	}

	newRule()
	{
		this.state.EditingKey = '(new)';
		this.state.RoleSearch.Draft = '';
		this.state.Draft = { Label: '', Roles: [], Values: [], TargetKey: ENTITIES[this.state.Entity].Targets[0].Key, CustomApp: 'LIMS', CustomRoute: '', Extra: { Audience: {}, When: {} } };
		this.render();
	}

	cancelEdit()
	{
		this.state.EditingKey = '';
		this.state.Draft = null;
		this.render();
	}

	setDraft(pField, pValue)
	{
		const tmpDraft = this.state.Draft;
		if (!tmpDraft)
		{
			return;
		}
		tmpDraft[pField] = pValue;
		if (pField === 'TargetKey')
		{
			this.render();
		}
	}

	toggleDraftList(pField, pValue)
	{
		const tmpDraft = this.state.Draft;
		if (!tmpDraft || !Array.isArray(tmpDraft[pField]))
		{
			return;
		}
		const tmpIndex = tmpDraft[pField].indexOf(pValue);
		if (tmpIndex >= 0)
		{
			tmpDraft[pField].splice(tmpIndex, 1);
		}
		else
		{
			tmpDraft[pField].push(pValue);
		}
	}

	/**
	 * @param {Object} pDraft
	 * @return {{ Rule?: DestinationRule, Error?: string }}
	 */
	buildRule(pDraft)
	{
		const tmpEntity = ENTITIES[this.state.Entity];
		const tmpLabel = String(pDraft.Label || '').trim();
		if (!tmpLabel)
		{
			return { Error: 'Give the rule a button label, e.g. “Open in LIMS”.' };
		}
		let tmpTarget = null;
		if (pDraft.TargetKey === CUSTOM_TARGET_KEY)
		{
			const tmpRoute = String(pDraft.CustomRoute || '').trim();
			if (!libNavigation.APPS[pDraft.CustomApp])
			{
				return { Error: 'Choose which app the custom destination opens.' };
			}
			if (!tmpRoute.startsWith('#/'))
			{
				return { Error: 'A custom route starts with #/, e.g. #/Sample/{~D:Record.GUIDSample~}.' };
			}
			tmpTarget = { App: pDraft.CustomApp, Route: tmpRoute };
		}
		else
		{
			const tmpPreset = tmpEntity.Targets.find((pChoice) => pChoice.Key === pDraft.TargetKey);
			if (!tmpPreset)
			{
				return { Error: 'Choose where the rule sends people.' };
			}
			tmpTarget = JSON.parse(JSON.stringify(tmpPreset.Target));
		}
		const tmpAudience = Object.assign({}, (pDraft.Extra && pDraft.Extra.Audience) || {});
		if (pDraft.Roles.length)
		{
			tmpAudience.Roles = pDraft.Roles.slice();
		}
		else
		{
			delete tmpAudience.Roles;
		}
		const tmpWhen = Object.assign({}, (pDraft.Extra && pDraft.Extra.When) || {});
		const tmpAddress = tmpEntity.Conditions[0].Address;
		const tmpValues = (Array.isArray(pDraft.Values) ? pDraft.Values : String(pDraft.Values || '').split(',')).map((pValue) => String(pValue).trim()).filter(Boolean);
		if (tmpValues.length)
		{
			tmpWhen[tmpAddress] = tmpValues;
		}
		else
		{
			delete tmpWhen[tmpAddress];
		}
		/** @type {DestinationRule} */
		const tmpRule = { Label: tmpLabel, Target: tmpTarget };
		if (Object.keys(tmpAudience).length)
		{
			tmpRule.Audience = tmpAudience;
		}
		if (Object.keys(tmpWhen).length)
		{
			tmpRule.When = tmpWhen;
		}
		return { Rule: tmpRule };
	}

	applyDraft()
	{
		const tmpState = this.state;
		const tmpBuilt = this.buildRule(tmpState.Draft || {});
		if (tmpBuilt.Error)
		{
			tmpState.Error = tmpBuilt.Error;
			this.render();
			return;
		}
		const tmpRules = this.currentRules();
		let tmpKey = tmpState.EditingKey;
		if (tmpKey === '(new)' || !tmpRules[tmpKey])
		{
			const tmpBase = tmpBuilt.Rule.Label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'rule';
			tmpKey = tmpBase;
			for (let i = 2; tmpRules[tmpKey] || this.isPlatformRule(tmpKey); i++)
			{
				tmpKey = `${tmpBase}-${i}`;
			}
			tmpBuilt.Rule.Order = Number.MAX_SAFE_INTEGER;
		}
		else
		{
			tmpBuilt.Rule.Order = tmpRules[tmpKey].Order;
			if (tmpRules[tmpKey].Enabled === false)
			{
				tmpBuilt.Rule.Enabled = false;
			}
		}
		tmpRules[tmpKey] = tmpBuilt.Rule;
		this._renumber(this.customerRules());
		tmpState.Error = '';
		tmpState.EditingKey = '';
		tmpState.Draft = null;
		this._markDirty();
		this.render();
	}

	// --- Save -----------------------------------------------------------------------------------------------

	async save()
	{
		const tmpState = this.state;
		const tmpEntities = Object.keys(tmpState.Dirty).filter((pEntity) => tmpState.Dirty[pEntity]);
		if (tmpState.Saving || tmpEntities.length === 0)
		{
			return;
		}
		tmpState.Saving = true;
		tmpState.Error = '';
		tmpState.Message = '';
		this.render();
		try
		{
			for (const tmpEntity of tmpEntities)
			{
				const tmpResult = await this._postJSON(`Configuration/Put/EntityDestinations/${tmpEntity}/Customer/${Number(tmpState.TargetCustomerID)}`, { Config: tmpState.Rules[tmpEntity] || {} });
				if (tmpResult.error || (tmpResult.result && tmpResult.result.Error))
				{
					throw new Error((tmpResult.error && tmpResult.error.message) || (tmpResult.result && tmpResult.result.Error) || String(tmpResult.error));
				}
				tmpState.Dirty[tmpEntity] = false;
			}
			tmpState.Message = `Saved. Scanned labels and links for ${tmpState.TargetCustomerName || 'this customer'} follow these rules from now on.`;
		}
		catch (pError)
		{
			tmpState.Error = `Save failed: ${(pError && pError.message) || pError}`;
		}
		finally
		{
			tmpState.Saving = false;
			this.render();
		}
	}

	// --- Preview --------------------------------------------------------------------------------------------

	// --- Role picker (a customer can have hundreds of roles, so they are searched, not listed) --------------

	/**
	 * @param {'Draft'|'Preview'} pContext
	 * @return {Array<string>} the chosen role hashes for the rule being edited, or for the preview
	 */
	_chosenRoles(pContext)
	{
		if (pContext === 'Draft')
		{
			return (this.state.Draft && Array.isArray(this.state.Draft.Roles)) ? this.state.Draft.Roles : [];
		}
		return this.state.PreviewRoles;
	}

	/**
	 * @param {'Draft'|'Preview'} pContext
	 * @param {string} pText
	 * @return {{ Matches: Array<{ Hash: string, Name: string }>, More: number }} roles not yet chosen whose name contains the text
	 */
	findRoles(pContext, pText)
	{
		const tmpChosen = this._chosenRoles(pContext);
		const tmpNeedle = String(pText || '').trim().toLowerCase();
		const tmpAll = this.state.Roles.filter((pRole) => !tmpChosen.includes(pRole.Hash) && (!tmpNeedle || pRole.Name.toLowerCase().includes(tmpNeedle)));
		return { Matches: tmpAll.slice(0, ROLE_SUGGESTION_LIMIT), More: Math.max(tmpAll.length - ROLE_SUGGESTION_LIMIT, 0) };
	}

	/**
	 * Show the matching roles under the search box, without redrawing the box being typed in.
	 * @param {'Draft'|'Preview'} pContext
	 * @param {string} pText
	 */
	searchRoles(pContext, pText)
	{
		this.state.RoleSearch[pContext] = String(pText || '');
		if (typeof document !== 'undefined' && document.getElementById(`EDC-RoleSuggest-${pContext}`))
		{
			this.pict.ContentAssignment.assignContent(`#EDC-RoleSuggest-${pContext}`, this.renderRoleSuggestionsHTML(pContext));
		}
	}

	/**
	 * @param {'Draft'|'Preview'} pContext
	 * @param {string} pHash
	 */
	addRole(pContext, pHash)
	{
		const tmpChosen = this._chosenRoles(pContext);
		if (!tmpChosen.includes(pHash))
		{
			tmpChosen.push(pHash);
		}
		this.state.RoleSearch[pContext] = '';
		this.render();
		if (typeof document !== 'undefined' && document.getElementById(`EDC-RoleSearch-${pContext}`))
		{
			document.getElementById(`EDC-RoleSearch-${pContext}`).focus();
		}
	}

	/**
	 * @param {'Draft'|'Preview'} pContext
	 * @param {string} pHash
	 */
	dropRole(pContext, pHash)
	{
		const tmpChosen = this._chosenRoles(pContext);
		const tmpIndex = tmpChosen.indexOf(pHash);
		if (tmpIndex >= 0)
		{
			tmpChosen.splice(tmpIndex, 1);
		}
		this.render();
	}

	setPreviewValue(pValue)
	{
		this.state.PreviewValue = String(pValue || '');
		this.render();
	}

	/**
	 * Resolve the entity's current (unsaved) rules for an example record and the chosen roles.
	 * @return {ReturnType<typeof libNavigation.resolveDestinations>}
	 */
	preview()
	{
		const tmpState = this.state;
		const tmpEntity = ENTITIES[tmpState.Entity];
		const tmpRecord = Object.assign({}, tmpEntity.Example);
		const tmpAddress = tmpEntity.Conditions[0].Address.replace(/^Record\./, '');
		if (tmpState.PreviewValue)
		{
			tmpRecord[tmpAddress] = tmpState.PreviewValue;
		}
		const tmpRoles = tmpState.Roles.filter((pRole) => tmpState.PreviewRoles.includes(pRole.Hash));
		const tmpEnvironment = libHosts.getEnvironmentFromHostname((typeof window !== 'undefined' && window.location) ? window.location.hostname : '');
		return libNavigation.resolveDestinations({ Rules: this.currentRules(), Data: { Record: tmpRecord, Document: (tmpState.Entity === 'Document') ? tmpRecord : undefined, Session: { UserRoles: tmpRoles } }, From: 'links', Environment: tmpEnvironment });
	}

	// --- Rendering ------------------------------------------------------------------------------------------

	/**
	 * @param {DestinationRule} pRule
	 * @return {string} the rule as a sentence: who, when, where
	 */
	describeRule(pRule)
	{
		const tmpState = this.state;
		const tmpEntity = ENTITIES[tmpState.Entity];
		const tmpRoleNames = ((pRule.Audience && pRule.Audience.Roles) || []).map((pHash) => (tmpState.Roles.find((pRole) => pRole.Hash === pHash) || { Name: pHash }).Name);
		const tmpWho = tmpRoleNames.length ? tmpRoleNames.join(', ') : 'Everyone';
		const tmpConditions = Object.entries(pRule.When || {}).map(([ pAddress, pValues ]) =>
		{
			const tmpCondition = tmpEntity.Conditions.find((pCandidate) => pCandidate.Address === pAddress);
			const tmpNames = (Array.isArray(pValues) ? pValues : [ pValues ]).map((pValue) => ((tmpCondition && tmpCondition.Values === 'Statuses') ? ((tmpState.Statuses.find((pStatus) => pStatus.Hash === pValue) || { Name: pValue }).Name) : pValue));
			return `${(tmpCondition ? tmpCondition.Label : pAddress).toLowerCase()} is ${tmpNames.join(' or ')}`;
		});
		const tmpPreset = tmpEntity.Targets.find((pChoice) => JSON.stringify(pChoice.Target) === JSON.stringify(pRule.Target || {}));
		const tmpWhere = tmpPreset ? tmpPreset.Label : `${(pRule.Target && pRule.Target.App) || '?'} ${(pRule.Target && pRule.Target.Route) || ''}`;
		return `${this._esc(tmpWho)}${tmpConditions.length ? `, when ${this._esc(tmpConditions.join(' and '))}` : ''} → <b>${this._esc(tmpWhere)}</b>`;
	}

	render()
	{
		const tmpContainer = (typeof document !== 'undefined') ? document.getElementById('EDC-Detail') : null;
		const tmpHTML = this.renderHTML();
		if (tmpContainer)
		{
			this.pict.ContentAssignment.assignContent('#EDC-Detail', tmpHTML);
		}
		return tmpHTML;
	}

	/** @return {string} */
	renderHTML()
	{
		const tmpState = this.state;
		if (!this.can())
		{
			return '<div class="edc-empty">You don’t have the “Configure where records open” entitlement.</div>';
		}
		if (tmpState.Loading)
		{
			return '<div class="edc-empty"><i class="fas fa-spinner fa-spin"></i> Loading…</div>';
		}
		const tmpEntity = ENTITIES[tmpState.Entity];
		const tmpCustomer = (this.isSuperUser() && tmpState.Customers.length)
			? `<select class="edc-select" onchange="_Pict.providers.EntityDestinationsConfig.selectCustomer(this.value)">${tmpState.Customers.map((pCustomer) => `<option value="${Number(pCustomer.IDCustomer)}" ${Number(pCustomer.IDCustomer) === Number(tmpState.TargetCustomerID) ? 'selected' : ''}>${this._esc(pCustomer.Name)}</option>`).join('')}</select>`
			: `<span class="edc-customer">${this._esc(tmpState.TargetCustomerName)}</span>`;
		const tmpTabs = Object.entries(ENTITIES).map(([ pKey, pEntity ]) => `<button class="edc-tab ${pKey === tmpState.Entity ? 'is-active' : ''}" onclick="_Pict.providers.EntityDestinationsConfig.setEntity('${pKey}')">${this._esc(pEntity.Label)}${tmpState.Dirty[pKey] ? ' •' : ''}</button>`).join('');
		const tmpOrdered = this.orderedRules();
		const tmpOwnKeys = this.customerRules().map(([ pKey ]) => pKey);
		const tmpRows = tmpOrdered.map(([ pKey, pRule ]) =>
		{
			const tmpKeyArg = this._esc(JSON.stringify(pKey));
			if (this.isPlatformRule(pKey))
			{
				return `<div class="edc-rule is-platform ${pRule.Enabled === false ? 'is-off' : ''}">
					<div class="edc-rule-order"></div>
					<div class="edc-rule-body">
						<div class="edc-rule-label">${this._esc(pRule.Label)} <span class="edc-badge" title="Set for every customer; tried after this customer’s own rules">Platform default</span>${pRule.Enabled === false ? ' <span class="edc-off">Off for this customer</span>' : ''}</div>
						<div class="edc-rule-sentence">${this.describeRule(pRule)}</div>
					</div>
					<div class="edc-rule-actions">
						<button class="edc-btn" onclick='_Pict.providers.EntityDestinationsConfig.toggleRule(${tmpKeyArg})'>${pRule.Enabled === false ? 'Turn on for this customer' : 'Turn off for this customer'}</button>
					</div>
				</div>`;
			}
			const tmpIndex = tmpOwnKeys.indexOf(pKey);
			return `<div class="edc-rule ${pRule.Enabled === false ? 'is-off' : ''}">
				<div class="edc-rule-order">
					<button class="edc-mv" title="Try earlier" ${tmpIndex === 0 ? 'disabled' : ''} onclick='_Pict.providers.EntityDestinationsConfig.moveRule(${tmpKeyArg}, -1)'><i class="fas fa-chevron-up"></i></button>
					<button class="edc-mv" title="Try later" ${tmpIndex === tmpOwnKeys.length - 1 ? 'disabled' : ''} onclick='_Pict.providers.EntityDestinationsConfig.moveRule(${tmpKeyArg}, 1)'><i class="fas fa-chevron-down"></i></button>
				</div>
				<div class="edc-rule-body">
					<div class="edc-rule-label">${this._esc(pRule.Label)}${pRule.Enabled === false ? ' <span class="edc-off">Off</span>' : ''}</div>
					<div class="edc-rule-sentence">${this.describeRule(pRule)}</div>
				</div>
				<div class="edc-rule-actions">
					<button class="edc-btn" onclick='_Pict.providers.EntityDestinationsConfig.editRule(${tmpKeyArg})'>Edit</button>
					<button class="edc-btn" onclick='_Pict.providers.EntityDestinationsConfig.toggleRule(${tmpKeyArg})'>${pRule.Enabled === false ? 'Turn on' : 'Turn off'}</button>
					<button class="edc-btn is-danger" onclick='_Pict.providers.EntityDestinationsConfig.removeRule(${tmpKeyArg})'>Remove</button>
				</div>
			</div>`;
		}).join('');
		const tmpList = tmpRows || `<div class="edc-note">No rules for ${this._esc(tmpEntity.Label.toLowerCase())} yet, so scanned labels and links open where they always have.</div>`;
		const tmpDirty = Object.values(tmpState.Dirty).some(Boolean);
		return `
			<div class="edc-field"><label class="edc-field-label">Customer</label>${tmpCustomer}</div>
			${tmpState.Message ? `<div class="edc-msg">${this._esc(tmpState.Message)}</div>` : ''}
			${tmpState.Error ? `<div class="edc-error">${this._esc(tmpState.Error)}</div>` : ''}
			<div class="edc-tabs">${tmpTabs}</div>
			<p class="edc-help">Rules are tried from the top. The first one that fits the person and the record decides where a scanned label or emailed link opens; the others are offered as alternatives. Platform defaults apply to every customer and are tried after this customer’s own rules.</p>
			<div class="edc-rules">${tmpList}</div>
			${tmpState.Draft ? this.renderEditorHTML() : `<button class="edc-btn" onclick="_Pict.providers.EntityDestinationsConfig.newRule()"><i class="fas fa-plus"></i> Add a rule</button>`}
			<div class="edc-savebar"><button class="edc-save" ${(!tmpDirty || tmpState.Saving) ? 'disabled' : ''} onclick="_Pict.providers.EntityDestinationsConfig.save()">${tmpState.Saving ? 'Saving…' : 'Save changes'}</button>${tmpDirty ? '<span class="edc-unsaved">Unsaved changes</span>' : ''}</div>
			${this.renderPreviewHTML()}`;
	}

	/** @return {string} */
	renderEditorHTML()
	{
		const tmpState = this.state;
		const tmpDraft = tmpState.Draft;
		const tmpEntity = ENTITIES[tmpState.Entity];
		const tmpCondition = tmpEntity.Conditions[0];
		const tmpValues = (tmpCondition.Values === 'Statuses')
			? tmpState.Statuses.map((pStatus) => `<label class="edc-chip"><input type="checkbox" ${tmpDraft.Values.includes(pStatus.Hash) ? 'checked' : ''} onchange='_Pict.providers.EntityDestinationsConfig.toggleDraftList("Values", ${this._esc(JSON.stringify(pStatus.Hash))})'> ${this._esc(pStatus.Name)}</label>`).join('')
			: `<input class="edc-input" value="${this._esc(tmpDraft.Values.join(', '))}" placeholder="Any; or a comma-separated list" onchange="_Pict.providers.EntityDestinationsConfig.setDraft('Values', this.value.split(','))">`;
		const tmpTargets = tmpEntity.Targets.concat([ { Key: CUSTOM_TARGET_KEY, Label: 'Another page (advanced)' } ]).map((pChoice) => `<option value="${pChoice.Key}" ${tmpDraft.TargetKey === pChoice.Key ? 'selected' : ''}>${this._esc(pChoice.Label)}</option>`).join('');
		const tmpCustom = (tmpDraft.TargetKey === CUSTOM_TARGET_KEY)
			? `<div class="edc-field"><label class="edc-field-label">App and page</label>
				<select class="edc-select edc-narrow" onchange="_Pict.providers.EntityDestinationsConfig.setDraft('CustomApp', this.value)">${Object.keys(libNavigation.APPS).map((pApp) => `<option ${tmpDraft.CustomApp === pApp ? 'selected' : ''}>${pApp}</option>`).join('')}</select>
				<input class="edc-input edc-route" value="${this._esc(tmpDraft.CustomRoute)}" placeholder="#/Sample/{~D:Record.GUIDSample~}" onchange="_Pict.providers.EntityDestinationsConfig.setDraft('CustomRoute', this.value)">
				<div class="edc-hint">Use <code>{~D:Record.Field~}</code> for values from the scanned record.</div></div>`
			: '';
		return `<div class="edc-editor">
			<div class="edc-field"><label class="edc-field-label">Button label</label><input class="edc-input" value="${this._esc(tmpDraft.Label)}" placeholder="Open in LIMS" onchange="_Pict.providers.EntityDestinationsConfig.setDraft('Label', this.value)"></div>
			<div class="edc-field"><label class="edc-field-label">Who (leave empty for everyone)</label>${this.renderRolePickerHTML('Draft', 'Everyone')}</div>
			<div class="edc-field"><label class="edc-field-label">When ${this._esc(tmpCondition.Label.toLowerCase())} is (leave empty for any)</label><div class="edc-chips">${tmpValues}</div></div>
			<div class="edc-field"><label class="edc-field-label">Open</label><select class="edc-select" onchange="_Pict.providers.EntityDestinationsConfig.setDraft('TargetKey', this.value)">${tmpTargets}</select></div>
			${tmpCustom}
			<div class="edc-editor-actions"><button class="edc-save" onclick="_Pict.providers.EntityDestinationsConfig.applyDraft()">Keep this rule</button> <button class="edc-btn" onclick="_Pict.providers.EntityDestinationsConfig.cancelEdit()">Cancel</button></div>
		</div>`;
	}

	/**
	 * The chosen roles as removable tags, and a search box that lists matching roles to add.
	 * @param {'Draft'|'Preview'} pContext
	 * @param {string} pNoneLabel - Shown when no role is chosen.
	 * @return {string}
	 */
	renderRolePickerHTML(pContext, pNoneLabel)
	{
		const tmpState = this.state;
		if (!tmpState.Roles.length)
		{
			return '<span class="edc-hint">No roles found for this customer.</span>';
		}
		const tmpTags = this._chosenRoles(pContext).map((pHash) =>
		{
			const tmpName = (tmpState.Roles.find((pRole) => pRole.Hash === pHash) || { Name: pHash }).Name;
			return `<span class="edc-tag">${this._esc(tmpName)}<button type="button" class="edc-tag-x" title="Remove" onclick='_Pict.providers.EntityDestinationsConfig.dropRole("${pContext}", ${this._esc(JSON.stringify(pHash))})'>×</button></span>`;
		}).join('');
		return `<div class="edc-picker">
			<div class="edc-tags">${tmpTags || `<span class="edc-hint">${this._esc(pNoneLabel)}</span>`}</div>
			<input id="EDC-RoleSearch-${pContext}" class="edc-input" autocomplete="off" placeholder="Find a role to add (${tmpState.Roles.length} roles)" value="${this._esc(tmpState.RoleSearch[pContext])}" oninput="_Pict.providers.EntityDestinationsConfig.searchRoles('${pContext}', this.value)" onfocus="_Pict.providers.EntityDestinationsConfig.searchRoles('${pContext}', this.value)">
			<div id="EDC-RoleSuggest-${pContext}" class="edc-suggest">${tmpState.RoleSearch[pContext] ? this.renderRoleSuggestionsHTML(pContext) : ''}</div>
		</div>`;
	}

	/**
	 * @param {'Draft'|'Preview'} pContext
	 * @return {string}
	 */
	renderRoleSuggestionsHTML(pContext)
	{
		const tmpFound = this.findRoles(pContext, this.state.RoleSearch[pContext]);
		if (!tmpFound.Matches.length)
		{
			return '<span class="edc-hint">No roles match.</span>';
		}
		const tmpButtons = tmpFound.Matches.map((pRole) => `<button type="button" class="edc-suggestion" onclick='_Pict.providers.EntityDestinationsConfig.addRole("${pContext}", ${this._esc(JSON.stringify(pRole.Hash))})'>${this._esc(pRole.Name)}</button>`).join('');
		return `${tmpButtons}${tmpFound.More ? `<span class="edc-hint">and ${tmpFound.More} more; keep typing to narrow it down.</span>` : ''}`;
	}

	/** @return {string} */
	renderPreviewHTML()
	{
		const tmpState = this.state;
		const tmpEntity = ENTITIES[tmpState.Entity];
		const tmpCondition = tmpEntity.Conditions[0];
		const tmpValue = (tmpCondition.Values === 'Statuses')
			? `<select class="edc-select edc-narrow" onchange="_Pict.providers.EntityDestinationsConfig.setPreviewValue(this.value)"><option value="">Any</option>${tmpState.Statuses.map((pStatus) => `<option value="${this._esc(pStatus.Hash)}" ${tmpState.PreviewValue === pStatus.Hash ? 'selected' : ''}>${this._esc(pStatus.Name)}</option>`).join('')}</select>`
			: `<input class="edc-input edc-narrow" value="${this._esc(tmpState.PreviewValue)}" onchange="_Pict.providers.EntityDestinationsConfig.setPreviewValue(this.value)">`;
		let tmpResultHTML = '';
		try
		{
			const tmpResult = this.preview();
			const tmpDescribe = (pDestination) => this._esc(pDestination.Label) + (pDestination.URL ? ` <code>${this._esc(pDestination.URL)}</code>` : '');
			tmpResultHTML = tmpResult.Primary
				? `<div><b>Opens:</b> ${tmpDescribe(tmpResult.Primary)}</div>${tmpResult.Alternatives.length ? `<div><b>Also offered:</b> ${tmpResult.Alternatives.map(tmpDescribe).join('; ')}</div>` : ''}`
				: '<div><b>No rule fits</b>, so the label opens where it always has.</div>';
			tmpResultHTML += `<details class="edc-explain"><summary>Why</summary><ul>${tmpResult.Explain.map((pLine) => `<li>${this._esc(pLine)}</li>`).join('')}</ul></details>`;
		}
		catch (pError)
		{
			tmpResultHTML = `<div class="edc-error">Preview unavailable: ${this._esc((pError && pError.message) || pError)}</div>`;
		}
		return `<div class="edc-preview">
			<div class="edc-field-label">Try it: who goes where</div>
			<div class="edc-field"><label class="edc-field-label">A person with these roles</label>${this.renderRolePickerHTML('Preview', 'No roles')}</div>
			<div class="edc-field"><label class="edc-field-label">${this._esc(tmpCondition.Label)}</label>${tmpValue}</div>
			<div class="edc-preview-result">${tmpResultHTML}</div>
		</div>`;
	}
}

module.exports = EntityDestinationsConfigProvider;
module.exports.ENTITIES = ENTITIES;
module.exports.default_configuration =
{
	ProviderIdentifier: 'EntityDestinationsConfig',
	AutoInitialize: true,
	AutoInitializeOrdinal: 232,
};
